import React, { useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useLossWriteoffClaims } from "@moonscape/shared";
import { Button } from "@/components/ui/button";

interface LossWriteoffModalProps {
  isOpen: boolean;
  onClose: () => void;
  asset: string;
  remainingQty: number;
  costBasis: number;
}

export const LossWriteoffModal: React.FC<LossWriteoffModalProps> = ({
  isOpen,
  onClose,
  asset,
  remainingQty,
  costBasis,
}) => {
  const [reason, setReason] = useState("");
  const { createMutation } = useLossWriteoffClaims();

  const handleSubmit = async () => {
    try {
      await createMutation.mutateAsync({ asset, reason: reason || undefined });
      onClose();
      setReason("");
    } catch (error) {
      // Error is already handled in the mutation
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon
                      className="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold leading-6 text-gray-900"
                    >
                      Mark {asset} as Loss Writeoff
                    </Dialog.Title>
                    <div className="mt-4 space-y-4">
                      <p className="text-sm text-gray-500">
                        This will mark all remaining holdings of {asset} as a total loss writeoff for tax purposes.
                      </p>

                      <div className="bg-gray-50 p-4 rounded-md space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Remaining Quantity:</span>
                          <span className="font-medium">{remainingQty.toFixed(8)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total Cost Basis:</span>
                          <span className="font-medium">${costBasis.toFixed(2)}</span>
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="reason"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Reason (optional)
                        </label>
                        <textarea
                          id="reason"
                          rows={3}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="e.g., Rug pull, project abandoned, exchange hacked..."
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                        />
                      </div>

                      <div className="bg-yellow-50 p-3 rounded-md">
                        <p className="text-xs text-yellow-800">
                          <strong>Note:</strong> This creates a synthetic sale transaction at $0 proceeds,
                          allowing you to claim the loss on your taxes. The cost basis will be preserved
                          for tax reporting.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                  <Button
                    variant="destructive"
                    onClick={handleSubmit}
                    disabled={createMutation.isLoading}
                    className="inline-flex w-full justify-center sm:w-auto"
                  >
                    {createMutation.isLoading ? "Processing..." : "Mark as Loss Writeoff"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onClose}
                    disabled={createMutation.isLoading}
                    className="mt-3 inline-flex w-full justify-center sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
